import React, { useState, useEffect, useCallback } from 'react';
import API_URL from '../config';
import SharedNav from './SharedNav';
import './BlogAgent.css';

export default function BlogAgent({ user, onLogout }) {
  const [activeTab, setActiveTab] = useState('write'); // 'write' | 'rulebook'

  // Rulebook state
  const [rulebook, setRulebook] = useState('');
  const [rulebookDraft, setRulebookDraft] = useState('');
  const [rulebookSaving, setRulebookSaving] = useState(false);
  const [rulebookSaved, setRulebookSaved] = useState(false);

  // Write state
  const [topic, setTopic] = useState('');
  const [guidelines, setGuidelines] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState('');

  // Preview / post state
  const [post, setPost] = useState(null);
  const [tagInput, setTagInput] = useState('');
  const [showRaw, setShowRaw] = useState(false);

  // Publish state
  const [publishing, setPublishing] = useState(false);
  const [publishResult, setPublishResult] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const loadRulebook = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/blog/rulebook`, { headers });
      const data = await res.json();
      setRulebook(data.rulebook || '');
      setRulebookDraft(data.rulebook || '');
    } catch (e) { /* ignore */ }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadRulebook(); }, [loadRulebook]);

  async function saveRulebook() {
    setRulebookSaving(true);
    setRulebookSaved(false);
    try {
      await fetch(`${API_URL}/api/blog/rulebook`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ rulebook: rulebookDraft }),
      });
      setRulebook(rulebookDraft);
      setRulebookSaved(true);
      setTimeout(() => setRulebookSaved(false), 2500);
    } catch (e) { /* ignore */ } finally {
      setRulebookSaving(false);
    }
  }

  async function generatePost() {
    if (!topic.trim()) return;
    setGenerating(true);
    setGenerateError('');
    setPost(null);
    setPublishResult(null);
    try {
      const res = await fetch(`${API_URL}/api/blog/generate`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ topic: topic.trim(), guidelines: guidelines.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Generation failed');
      setPost(data.post);
    } catch (e) {
      setGenerateError(e.message);
    } finally {
      setGenerating(false);
    }
  }

  async function publishPost(status) {
    if (!post) return;
    setPublishing(true);
    setPublishResult(null);
    try {
      const res = await fetch(`${API_URL}/api/blog/publish`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ post, status }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Publish failed');
      setPublishResult({ ok: true, url: data.url, status: data.status });
    } catch (e) {
      setPublishResult({ ok: false, error: e.message });
    } finally {
      setPublishing(false);
    }
  }

  function addTag(e) {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      const newTag = tagInput.trim();
      if (!post.tags.includes(newTag) && post.tags.length < 5) {
        setPost(p => ({ ...p, tags: [...p.tags, newTag] }));
      }
      setTagInput('');
    }
  }

  function removeTag(tag) {
    setPost(p => ({ ...p, tags: p.tags.filter(t => t !== tag) }));
  }

  return (
    <div className="blog-agent-page">
      <SharedNav user={user} onLogout={onLogout} />
      <div className="blog-agent-body">

        {/* ── Left Panel ── */}
        <div className="ba-left">
          <div className="ba-left-tabs">
            <button
              className={`ba-tab${activeTab === 'write' ? ' ba-tab--active' : ''}`}
              onClick={() => setActiveTab('write')}
            >Write Post</button>
            <button
              className={`ba-tab${activeTab === 'rulebook' ? ' ba-tab--active' : ''}`}
              onClick={() => setActiveTab('rulebook')}
            >Rulebook</button>
          </div>

          {activeTab === 'rulebook' && (
            <div className="ba-panel">
              <div className="ba-rulebook-info">
                Rules here are injected into every post generation. Use this to define writing style,
                tone, topics to avoid, brand voice, and required disclaimers.
              </div>
              <div>
                <div className="ba-label">Content Rules</div>
                <textarea
                  className="ba-textarea ba-textarea--rulebook"
                  value={rulebookDraft}
                  onChange={e => setRulebookDraft(e.target.value)}
                  placeholder="e.g. Always write in second person. Avoid mentioning competitor names. Include a call-to-action in every conclusion..."
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <button
                  className="ba-save-btn"
                  onClick={saveRulebook}
                  disabled={rulebookSaving || rulebookDraft === rulebook}
                >
                  {rulebookSaving ? 'Saving…' : 'Save Rulebook'}
                </button>
                {rulebookSaved && <span className="ba-success-msg">✓ Saved</span>}
              </div>
            </div>
          )}

          {activeTab === 'write' && (
            <div className="ba-panel">
              <div>
                <div className="ba-label">Topic *</div>
                <input
                  className="ba-input"
                  value={topic}
                  onChange={e => setTopic(e.target.value)}
                  placeholder="e.g. How to recruit FedEx Ground drivers in rural markets"
                  onKeyDown={e => { if (e.key === 'Enter') generatePost(); }}
                />
              </div>
              <div>
                <div className="ba-label">Post Guidelines (optional)</div>
                <textarea
                  className="ba-textarea ba-textarea--guidelines"
                  value={guidelines}
                  onChange={e => setGuidelines(e.target.value)}
                  placeholder="e.g. Focus on ISPs in the Midwest. Include tips on Indeed job postings. Target audience: ISP owners."
                />
              </div>
              {rulebook && (
                <div className="ba-rulebook-info" style={{ fontSize: 11 }}>
                  ✓ Rulebook active ({rulebook.length} chars)
                </div>
              )}
              {generateError && <div className="ba-error">{generateError}</div>}
              <button
                className="ba-generate-btn"
                onClick={generatePost}
                disabled={generating || !topic.trim()}
              >
                {generating && <span className="ba-spinner" />}
                {generating ? 'Generating…' : '✨ Generate Post'}
              </button>
            </div>
          )}
        </div>

        {/* ── Right Panel ── */}
        <div className="ba-right">
          {!post ? (
            <div className="ba-right-empty">
              <div className="ba-right-empty-icon">✍️</div>
              <p>Enter a topic and click Generate Post to create your blog post.</p>
            </div>
          ) : (
            <div className="ba-preview">
              <div className="ba-preview-header">
                <span className="ba-preview-title">Post Preview</span>
                <div className="ba-preview-actions">
                  {publishResult && (
                    <span className={`ba-publish-result${publishResult.ok ? ' ba-publish-result--ok' : ' ba-publish-result--err'}`}>
                      {publishResult.ok
                        ? <>✓ {publishResult.status === 'draft' ? 'Saved as draft' : 'Published'} — <a href={publishResult.url} target="_blank" rel="noopener noreferrer">View</a></>
                        : `Error: ${publishResult.error}`}
                    </span>
                  )}
                  <button
                    className="ba-draft-btn"
                    onClick={() => publishPost('draft')}
                    disabled={publishing}
                  >
                    {publishing ? <span className="ba-spinner" /> : null}
                    Save as Draft
                  </button>
                  <button
                    className="ba-publish-btn"
                    onClick={() => publishPost('publish')}
                    disabled={publishing}
                  >
                    {publishing ? <span className="ba-spinner" /> : null}
                    Publish Live
                  </button>
                </div>
              </div>

              <div className="ba-preview-body">
                {/* Meta fields */}
                <div className="ba-meta-grid">
                  <div className="ba-field ba-field-full">
                    <div className="ba-label">Title</div>
                    <input
                      className="ba-input"
                      value={post.title || ''}
                      onChange={e => setPost(p => ({ ...p, title: e.target.value }))}
                    />
                  </div>
                  <div className="ba-field">
                    <div className="ba-label">Slug</div>
                    <input
                      className="ba-input"
                      value={post.slug || ''}
                      onChange={e => setPost(p => ({ ...p, slug: e.target.value }))}
                    />
                  </div>
                  <div className="ba-field">
                    <div className="ba-label">Excerpt</div>
                    <input
                      className="ba-input"
                      value={post.excerpt || ''}
                      onChange={e => setPost(p => ({ ...p, excerpt: e.target.value }))}
                    />
                  </div>
                  <div className="ba-field ba-field-full">
                    <div className="ba-label">Meta Description</div>
                    <input
                      className="ba-input"
                      value={post.meta_description || ''}
                      onChange={e => setPost(p => ({ ...p, meta_description: e.target.value }))}
                    />
                    <div style={{ fontSize: 11, color: (post.meta_description || '').length > 155 ? '#dc2626' : '#94a3b8', textAlign: 'right' }}>
                      {(post.meta_description || '').length}/155
                    </div>
                  </div>
                  <div className="ba-field ba-field-full">
                    <div className="ba-label">Tags (press Enter to add, max 5)</div>
                    <div className="ba-tags-row">
                      {(post.tags || []).map(tag => (
                        <span key={tag} className="ba-tag">
                          {tag}
                          <button className="ba-tag-remove" onClick={() => removeTag(tag)}>×</button>
                        </span>
                      ))}
                      {(post.tags || []).length < 5 && (
                        <input
                          className="ba-tag-input"
                          value={tagInput}
                          onChange={e => setTagInput(e.target.value)}
                          onKeyDown={addTag}
                          placeholder="Add tag…"
                        />
                      )}
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="ba-content-box">
                  <div className="ba-content-box-header">
                    <span className="ba-content-box-label">Content</span>
                    <button className="ba-toggle-view-btn" onClick={() => setShowRaw(v => !v)}>
                      {showRaw ? 'Preview' : 'Edit HTML'}
                    </button>
                  </div>
                  {showRaw ? (
                    <textarea
                      className="ba-content-textarea"
                      value={post.content || ''}
                      onChange={e => setPost(p => ({ ...p, content: e.target.value }))}
                    />
                  ) : (
                    <div
                      className="ba-content-rendered"
                      dangerouslySetInnerHTML={{ __html: post.content || '' }}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
